if (!customElements.get('mat-tier-picker')) {
  customElements.define(
    'mat-tier-picker',
    class MatTierPicker extends HTMLElement {
      static VALUE_PATTERN = /^\s*(\d+)\s*x\s+(\d+(?:\.\d+)?)\s*mm\b/i;

      constructor() {
        super();
        this.isSyncing = false;
        this.isReady = false;
        this.onThicknessChange = this.onThicknessChange.bind(this);
        this.onQuantityStepperChange = this.onQuantityStepperChange.bind(this);
        this.onSourceChange = this.onSourceChange.bind(this);
        this.onThicknessCardChange = this.onThicknessCardChange.bind(this);
        this.onPricingRowClick = this.onPricingRowClick.bind(this);
      }

      connectedCallback() {
        this.variantSelects = document.getElementById(`variant-selects-${this.dataset.section}`);
        this.thicknessSelect = this.querySelector('[data-mat-thickness]');
        this.summary = this.querySelector('[data-mat-summary]');
        this.quantityStepper = this.querySelector('[data-mat-tier-quantity-stepper] input');
        this.thicknessCards = this.querySelector('[data-mat-thickness-cards]');
        this.pricingRows = this.querySelector('[data-mat-pricing-rows]');
        this.summaryCount = this.querySelector('[data-mat-summary-count]');
        this.summaryRate = this.querySelector('[data-mat-summary-rate]');
        this.summaryTotal = this.querySelector('[data-mat-summary-total]');
        this.summaryNote = this.querySelector('[data-mat-summary-note]');
        this.cartQuantityInput = document.getElementById(`Quantity-${this.dataset.section}`);

        if (!this.variantSelects || !this.thicknessSelect || !this.quantityStepper) return;

        this.sourceSelect = this.variantSelects.querySelector('[data-variant-dropdown-select]');
        if (!this.sourceSelect) return;

        this.matrix = this.buildMatrix();
        if (!this.matrix) return;

        this.renderThicknessOptions();
        this.renderThicknessCards();
        this.syncFromSource();

        this.sourceSelect.closest('.product-form__input')?.classList.add('hidden');
        this.hidden = false;
        this.isReady = true;

        this.thicknessSelect.addEventListener('change', this.onThicknessChange);
        this.quantityStepper.addEventListener('change', this.onQuantityStepperChange);
        this.variantSelects.addEventListener('change', this.onSourceChange);
        this.thicknessCards?.addEventListener('change', this.onThicknessCardChange);
        this.pricingRows?.addEventListener('click', this.onPricingRowClick);
        this.quantityUnsubscribe = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.restoreSelection());
        this.variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.sectionId === this.dataset.section) this.restoreSelection();
        });
      }

      disconnectedCallback() {
        this.thicknessSelect?.removeEventListener('change', this.onThicknessChange);
        this.quantityStepper?.removeEventListener('change', this.onQuantityStepperChange);
        this.variantSelects?.removeEventListener('change', this.onSourceChange);
        this.thicknessCards?.removeEventListener('change', this.onThicknessCardChange);
        this.pricingRows?.removeEventListener('click', this.onPricingRowClick);
        this.sourceSelect?.closest('.product-form__input')?.classList.remove('hidden');
        this.quantityUnsubscribe?.();
        this.variantUnsubscribe?.();
        this.isReady = false;
      }

      renderThicknessCards() {
        if (!this.thicknessCards) return;
        this.thicknessCards.replaceChildren();

        for (const [thickness] of this.matrix) {
          const unit = this.getUnitTier(thickness);
          const item = document.createElement('div');
          item.className = 'variant-card-picker__item';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = `mat-thickness-${this.dataset.section}`;
          input.id = `MatThicknessCard-${this.dataset.section}-${thickness}`;
          input.value = thickness;
          input.disabled = !unit.available;
          const label = document.createElement('label');
          label.htmlFor = input.id;
          label.className = 'variant-card-picker__card mat-thickness-card';
          label.innerHTML = `<svg class="mat-thickness-card__check" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
            <circle cx="10" cy="10" r="10" fill="currentColor"/>
            <path d="m5 10 3 3 7-7" fill="none" stroke="#fff" stroke-width="2"/>
          </svg>`;
          if (unit.image) {
            const image = document.createElement('img');
            image.className = 'mat-thickness-card__photo';
            image.src = `${unit.image}${unit.image.includes('?') ? '&' : '?'}width=320`;
            image.alt = '';
            image.width = 240;
            image.height = 180;
            image.loading = 'lazy';
            image.decoding = 'async';
            label.prepend(image);
          }
          const name = document.createElement('span');
          name.className = 'mat-thickness-card__name';
          name.textContent = thickness;
          label.append(name);
          if (!unit.available) {
            const stock = document.createElement('small');
            stock.className = 'mat-thickness-card__stock';
            stock.textContent = this.dataset.stringSoldOut;
            label.append(stock);
          }
          item.append(input, label);
          this.thicknessCards.append(item);
        }
      }

      updateThicknessDisplay(thickness) {
        this.querySelector('[data-mat-selected-thickness]').textContent = thickness;
        this.querySelector('[data-mat-pricing-thickness]').textContent = thickness;
        this.thicknessCards?.querySelectorAll('input').forEach((input) => {
          input.checked = input.value === thickness;
        });
      }

      onThicknessCardChange(event) {
        if (!event.target.matches('input[type="radio"]') || event.target.disabled) return;
        this.thicknessSelect.value = event.target.value;
        this.onThicknessChange();
      }

      buildMatrix() {
        let variants;
        try {
          const json = this.variantSelects.querySelector('[type="application/json"]');
          variants = JSON.parse(json.textContent);
        } catch (error) {
          console.error('[mat-tier-picker] Unable to parse variant data.', error);
          return null;
        }

        if (this.variantSelects.querySelectorAll('select, fieldset').length > 1) {
          console.warn('[mat-tier-picker] Expected a single-option product; skipping.');
          return null;
        }

        const matrix = new Map();
        const seen = new Set();

        for (const variant of variants) {
          const match = MatTierPicker.VALUE_PATTERN.exec(variant.option1 || '');
          if (!match) {
            console.warn(`[mat-tier-picker] Unrecognised option value "${variant.option1}"; skipping.`);
            return null;
          }

          const quantity = parseInt(match[1], 10);
          const thickness = `${match[2]}mm`;
          const dedupeKey = `${thickness}|${quantity}`;
          if (seen.has(dedupeKey)) {
            console.warn(`[mat-tier-picker] Duplicate tier for ${dedupeKey}; skipping.`);
            return null;
          }
          seen.add(dedupeKey);

          if (!matrix.has(thickness)) matrix.set(thickness, []);
          matrix.get(thickness).push({
            quantity,
            variantId: variant.id,
            image: variant.featured_image?.src || null,
            optionValue: variant.option1,
            available: variant.available,
            totalCents: variant.price,
            perMatCents: Math.round(variant.price / quantity),
          });
        }

        if (matrix.size < 2) {
          console.warn('[mat-tier-picker] Fewer than two thicknesses found; skipping.');
          return null;
        }

        const sorted = new Map(
          [...matrix.entries()]
            .sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]))
            .map(([thickness, tiers]) => [thickness, tiers.sort((a, b) => a.quantity - b.quantity)])
        );

        for (const tiers of sorted.values()) {
          const unit = tiers.find((tier) => tier.quantity === 1);
          if (!unit) return null;
          for (const tier of tiers) tier.perMatCents = Math.min(tier.perMatCents, unit.perMatCents);
        }
        return sorted;
      }

      renderThicknessOptions() {
        this.thicknessSelect.innerHTML = '';
        for (const [thickness, tiers] of this.matrix) {
          const option = document.createElement('option');
          option.value = thickness;
          option.textContent = thickness;
          if (!this.getUnitTier(thickness)?.available) option.disabled = true;
          this.thicknessSelect.append(option);
        }
      }

      renderPricingTable(thickness) {
        if (!this.pricingRows) return;
        this.pricingRows.innerHTML = '';

        const tiers = this.matrix.get(thickness) || [];
        for (const tier of tiers) {
          const row = document.createElement('tr');
          row.className = 'mat-pricing-table__row';
          row.dataset.matPricingRow = '';
          row.dataset.quantity = String(tier.quantity);

          const qtyCell = document.createElement('td');
          qtyCell.colSpan = 2;
          const chooseQuantity = document.createElement('button');
          chooseQuantity.type = 'button';
          chooseQuantity.className = 'mat-pricing-table__tier-button';
          const count = document.createElement('span');
          count.className = 'mat-pricing-table__tier-count';
          count.textContent = this.dataset.stringTierCount.replace('[count]', `${tier.quantity}+`);
          const rate = document.createElement('span');
          rate.className = 'mat-pricing-table__tier-rate';
          const price = document.createElement('strong');
          price.textContent = this.formatMoney(tier.perMatCents);
          const unit = document.createElement('small');
          unit.textContent = this.dataset.stringPerMat;
          rate.append(price, unit);
          chooseQuantity.append(count, rate);
          chooseQuantity.setAttribute('aria-label', `${count.textContent} · ${this.formatMoney(tier.perMatCents)} ${this.dataset.stringPerMat}`);
          qtyCell.append(chooseQuantity);
          row.append(qtyCell);
          this.pricingRows.append(row);
        }
      }

      highlightPricingRow(quantity) {
        this.pricingRows?.querySelectorAll('[data-mat-pricing-row]').forEach((row) => {
          const active = Number(row.dataset.quantity) === quantity;
          row.classList.toggle('mat-pricing-table__row--active', active);
          row.querySelector('button')?.setAttribute('aria-pressed', String(active));
        });
      }

      onPricingRowClick(event) {
        const row = event.target.closest('[data-mat-pricing-row]');
        if (!row) return;
        this.quantityStepper.value = row.dataset.quantity;
        this.quantityStepper.dispatchEvent(new Event('change', { bubbles: true }));
      }

      getTierByQuantity(thickness, quantity) {
        return (this.matrix.get(thickness) || []).find((tier) => tier.quantity === quantity);
      }

      // The single-mat variant is the only one ever added to the cart; every
      // other variant is only read for its per-mat price bracket.
      getUnitTier(thickness) {
        const tiers = this.matrix.get(thickness) || [];
        return tiers.find((tier) => tier.quantity === 1);
      }

      // Price bracket that applies to `count` mats: the largest bracket at or
      // below the count (e.g. 12 mats -> the 8+ bracket).
      resolveTier(thickness, count) {
        const tiers = this.matrix.get(thickness) || [];
        return [...tiers].reverse().find((tier) => tier.quantity <= count) || tiers[0];
      }

      // Pricing for any count of mats at a thickness.
      getPricing(thickness, count) {
        const tier = this.resolveTier(thickness, count);
        return {
          thickness,
          count,
          tier,
          perMatCents: tier.perMatCents,
          totalCents: tier.perMatCents * count,
        };
      }

      readCount() {
        const value = Number(this.quantityStepper.value);
        return Number.isSafeInteger(value) && value >= 1 ? value : 1;
      }

      onThicknessChange() {
        const thickness = this.thicknessSelect.value;
        this.renderPricingTable(thickness);
        this.applyCount(thickness, this.readCount());
      }

      onQuantityStepperChange() {
        this.applyCount(this.thicknessSelect.value, this.readCount());
      }

      applyCount(thickness, count) {
        count = Number.isSafeInteger(count) && count >= 1 ? count : 1;
        if (!this.matrix.has(thickness)) return;
        const pricing = this.getPricing(thickness, count);
        const unitTier = this.getUnitTier(thickness);

        if (this.quantityStepper.value !== String(count)) {
          this.quantityStepper.value = String(count);
        }

        // Real add-to-cart form: always the 1x variant, with the chosen count
        // as the line quantity.
        if (this.sourceSelect.value !== unitTier.optionValue) {
          this.isSyncing = true;
          this.sourceSelect.value = unitTier.optionValue;
          this.sourceSelect.dispatchEvent(new Event('change', { bubbles: true }));
          this.isSyncing = false;
        }
        this.syncCartQuantity(count);

        this.updateSummary(pricing);
        this.updateThicknessDisplay(thickness);
        this.highlightPricingRow(pricing.tier.quantity);
        this.dispatchEvent(
          new CustomEvent('mat-tier-picker:change', {
            bubbles: true,
            detail: { thickness, tier: pricing.tier, count, perMatCents: pricing.perMatCents, totalCents: pricing.totalCents },
          })
        );
      }

      getSelectedCartItem() {
        if (!this.isReady) return null;
        const unit = this.getUnitTier(this.getCurrentThickness());
        if (!unit?.available) throw new Error(this.dataset.stringSoldOut);
        return { id: unit.variantId, quantity: this.readCount() };
      }

      restoreSelection() {
        if (!this.isReady) return;
        this.syncCartQuantity(this.readCount());
        this.updateSummary(this.getPricing(this.getCurrentThickness(), this.readCount()));
      }

      syncCartQuantity(count) {
        const input = this.cartQuantityInput;
        if (!input) return;
        input.removeAttribute('max');
        input.removeAttribute('data-max');
        input.value = String(count);
      }

      updateSummary(pricing) {
        const { thickness, count, tier, perMatCents, totalCents } = pricing;
        if (this.summaryCount) {
          this.summaryCount.textContent = this.dataset.stringSummaryCount
            .replace('[count]', count)
            .replace('[thickness]', thickness);
        }
        const priceRoot = document.getElementById(`price-${this.dataset.section}`);
        priceRoot?.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale').forEach((element) => {
          element.textContent = this.formatMoney(perMatCents);
        });
        priceRoot?.querySelector('.price')?.classList.remove('price--on-sale');
        const vat = priceRoot?.querySelector('[data-price-vat-amount]');
        if (vat) vat.textContent = this.formatMoney(Math.round(perMatCents * 5 / 6));
        if (this.summaryRate) this.summaryRate.textContent = this.formatMoney(perMatCents);
        if (this.summaryTotal) this.summaryTotal.textContent = this.formatMoney(totalCents);
        if (this.summaryNote) {
          this.summaryNote.textContent = this.dataset.stringSummaryNote.replace('[bracket]', tier.quantity);
        }
      }

      syncFromSource() {
        const currentValue = this.sourceSelect.value;
        for (const [thickness, tiers] of this.matrix) {
          const tier = tiers.find((candidate) => candidate.optionValue === currentValue);
          if (!tier) continue;
          this.thicknessSelect.value = thickness;
          this.renderPricingTable(thickness);
          this.applyCount(thickness, tier.quantity);
          return;
        }

        // Fall back to the first thickness if the source value has no match yet.
        const [firstThickness] = this.matrix.entries().next().value;
        this.thicknessSelect.value = firstThickness;
        this.renderPricingTable(firstThickness);
        this.applyCount(firstThickness, 1);
      }

      onSourceChange() {
        if (this.isSyncing) return;
        this.syncFromSource();
      }

      formatMoney(cents) {
        const formatter = this.variantSelects?.querySelector('variant-card-picker');
        if (formatter?.formatMoney) return formatter.formatMoney(cents);
        return (cents / 100).toFixed(2);
      }

      // Public API for the mat coverage calculator.
      getCurrentThickness() {
        return this.thicknessSelect?.value;
      }

      getThicknesses() {
        return [...(this.matrix?.keys() || [])];
      }

      getTiers(thickness = this.getCurrentThickness()) {
        return [...(this.matrix?.get(thickness) || [])];
      }

      getUnitAvailable(thickness) {
        return Boolean(this.getUnitTier(thickness)?.available);
      }

      getPricingFor(thickness, count) {
        return this.getPricing(thickness, count);
      }

      selectQuantity(count) {
        if (!this.isReady) return;
        this.applyCount(this.getCurrentThickness(), count);

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.quantityStepper.focus();
        this.quantityStepper.scrollIntoView({
          block: 'center',
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      }

      // Selects the given thickness/count (keeping this picker's visible state
      // in sync) and submits the real add-to-cart form, exactly as if the
      // shopper had clicked "Add to cart" themselves. Used by the coverage
      // calculator's per-thickness buttons. Returns the variant id added, or null.
      addToCartForThickness(thickness, count) {
        if (!this.isReady) return null;
        const unitTier = this.getUnitTier(thickness);
        if (!unitTier || !unitTier.available) return null;

        if (this.thicknessSelect.value !== thickness) {
          this.thicknessSelect.value = thickness;
          this.renderPricingTable(thickness);
        }
        this.applyCount(thickness, count);

        const form = document.getElementById(`product-form-${this.dataset.section}`);
        const addButton = form?.querySelector('[name="add"]');
        addButton?.click();

        return unitTier.variantId;
      }
    }
  );
}
